/**
 * EstimatorClient — same-origin `/api/*` HTTP service (Task 10.1).
 *
 * All calls are same-origin under `/api/` (Req 14.2). Distinct error types let
 * the UI implement model-reselect recovery on 404/409 (Task 11.3, Req 9.8).
 */

import type { EstimateInput, EstimateResult, EstimationParams, ModelInfo, PresignedUpload } from '@tcc/shared';

/** Full configurable set returned by GET /api/config. */
export interface AppConfig {
	models: ModelInfo[];
	params: EstimationParams;
}

export async function getConfig(): Promise<AppConfig> {
	const res = await fetch('/api/config');
	if (!res.ok) throw new Error(`Failed to load config (${res.status})`);
	return (await res.json()) as AppConfig;
}

/** A field-level validation error returned by the API (400). */
export interface ApiFieldError {
	field: string;
	message: string;
}

/** Thrown on a 400 validation response. */
export class ValidationApiError extends Error {
	constructor(public readonly fields: ApiFieldError[]) {
		super('Validation failed');
		this.name = 'ValidationApiError';
	}
}

/** Thrown on a 404 (unknown model) or 409 (inactive model) response. */
export class ModelUnavailableError extends Error {
	constructor(
		public readonly status: number,
		public readonly modelId: string | undefined,
	) {
		super(status === 404 ? 'Model not found' : 'Model inactive');
		this.name = 'ModelUnavailableError';
	}
}

/** Thrown on a 503 (document store unavailable) response. */
export class ServiceUnavailableError extends Error {
	constructor(public readonly retryAfterSeconds: number) {
		super('Service temporarily unavailable');
		this.name = 'ServiceUnavailableError';
	}
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export async function listModels(): Promise<ModelInfo[]> {
	const res = await fetch('/api/models');
	if (!res.ok) {
		throw new Error(`Failed to load models (${res.status})`);
	}
	const data = (await res.json()) as { models: ModelInfo[] };
	return data.models;
}

export async function calculateEstimate(input: EstimateInput): Promise<EstimateResult> {
	const res = await fetch('/api/estimate', {
		method: 'POST',
		headers: JSON_HEADERS,
		body: JSON.stringify(input),
	});

	if (res.ok) {
		return (await res.json()) as EstimateResult;
	}

	if (res.status === 400) {
		const body = (await res.json().catch(() => ({}))) as { fields?: ApiFieldError[] };
		throw new ValidationApiError(body.fields ?? []);
	}
	if (res.status === 404 || res.status === 409) {
		const body = (await res.json().catch(() => ({}))) as { modelId?: string };
		throw new ModelUnavailableError(res.status, body.modelId);
	}
	throw new Error(`Estimate request failed (${res.status})`);
}

export async function getUploadUrl(fileName: string, contentType: string): Promise<PresignedUpload> {
	const res = await fetch('/api/uploads', {
		method: 'POST',
		headers: JSON_HEADERS,
		body: JSON.stringify({ fileName, contentType }),
	});
	if (res.status === 503) {
		const body = (await res.json().catch(() => ({}))) as { retryAfterSeconds?: number };
		throw new ServiceUnavailableError(body.retryAfterSeconds ?? 5);
	}
	if (!res.ok) {
		throw new Error(`Failed to get upload URL (${res.status})`);
	}
	return (await res.json()) as PresignedUpload;
}

/** Upload a file directly to S3 via a presigned PUT URL. */
export async function putToPresignedUrl(uploadUrl: string, file: File): Promise<void> {
	const res = await fetch(uploadUrl, {
		method: 'PUT',
		headers: { 'Content-Type': file.type || 'application/octet-stream' },
		body: file,
	});
	if (!res.ok) {
		throw new Error(`Upload failed (${res.status})`);
	}
}
